import { Box3, Matrix4, OrthographicCamera, Vector3 } from 'three';
import { ThreeDMouseControl } from './3d-mouse-control';
import { EPS, STATE } from './camera-controls-constants';
import { CameraMoveType, DefaultCameraPositionParams, ViewDirectionPreset } from './camera-controls.interface';
import { MathUtils } from '../../util/math-utils';
import { CameraControlEventHandler } from './camera-control-event-handler';
import { ArticleContainer } from '@3d-scene/model3d/article-container';

export class CameraControl {
  private readonly _xAxis = Object.freeze(new Vector3(1, 0, 0));
  private readonly _yAxis = Object.freeze(new Vector3(0, 1, 0));
  private readonly _zAxis = Object.freeze(new Vector3(0, 0, 1));
  private readonly _padding = 25;
  private readonly _allObjectsPadding: number = 150;

  private _aspect = 0;
  private _eventHandler: CameraControlEventHandler;
  private _defaultCameraMatrix: Matrix4;

  constructor(
    threeDMouseConnector: ThreeDMouseControl,
    private canvas: HTMLCanvasElement,
    private camera: OrthographicCamera,
    private renderScene: () => void,
    private updateWirecontourVisibility: () => void,
  ) {
    this._aspect = canvas.clientWidth / canvas.clientHeight;
    threeDMouseConnector.connect();

    this._eventHandler = new CameraControlEventHandler(
      canvas,
      camera,
      () => renderScene(),
      () => updateWirecontourVisibility(),
      () => this.update(),
      (panAngleDeg: number, tiltAngleDeg: number) => this.rotateCamera(panAngleDeg, tiltAngleDeg),
      (value: number) => this.zoomCamera(value),
      (x: number, y: number) => this.moveCamera(x, y)
    );

    this.reset();

    this._defaultCameraMatrix = this.camera.matrixWorld.clone();
  }

  public get currentCameraMatrix(): Matrix4 {
    return this.camera.matrixWorld.clone();
  }

  public get defaultCameraMatrix(): Matrix4 {
    return this._defaultCameraMatrix.clone();
  }

  public get hideSceneObjects(): boolean {
    return this._eventHandler.hideSceneObjects;
  }

  public set hideSceneObjects(hide: boolean) {
    this._eventHandler.hideSceneObjects = hide;
  }

  public get hiddenSceneObjects(): ArticleContainer[] | undefined {
    return this._eventHandler.hiddenSceneObjects;
  }

  public set hiddenSceneObjects(objects: ArticleContainer[]) {
    this._eventHandler.hiddenSceneObjects = objects;
  }

  public get enabled(): boolean {
    return this._eventHandler.enabled;
  }

  public set enabled(enabled: boolean) {
    this._eventHandler.enabled = enabled;
  }

  public getMode(): CameraMoveType {
    return this._eventHandler.getMode();
  }

  private reset(): void {
    this.camera.updateProjectionMatrix();
    this.camera.position.set(0, 0, 0);
    this.camera.zoom = 1;

    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    this.camera.left = -width / 2;
    this.camera.right = width / 2;
    this.camera.top = height / 2;
    this.camera.bottom = -height / 2;

    this.hideSceneObjects = false;

    this.resetRotation();
    this.update();
  }

  private resetRotation(): void {
    this.camera.rotation.set(0, 0, 0);
  }

  private update(): void {
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld(true);
    this.updateWirecontourVisibility();
    this.renderScene();
  }

  public dispose(): void {
    this._eventHandler.dispose();
  }

  public changeMode(mode: CameraMoveType): void {
    this._eventHandler.changeMode(mode);
  }

  public setControlsState(state: STATE): void {
    this._eventHandler.setControlsState(state);
  }

  public move(x: number, y: number): void {
    this.moveCamera(x, y);
    this.update();
  }

  public moveCamera(x: number, y: number, z?: number): void {
    this.camera.translateOnAxis(this._xAxis, -x || 0);
    this.camera.translateOnAxis(this._yAxis, y || 0);
    this.camera.translateOnAxis(this._zAxis, z || 0);
  }

  public view(view: ViewDirectionPreset, offset?: {left?: number, top?: number}): void {
    this.resetRotation();
    switch (view) {
      case 'isometric':
        this.rotateCamera(135, 35.27122061);
        break;
      case 'front':
        this.rotateCamera(180, EPS);
        break;
      case 'back':
        this.rotateCamera(360, EPS);
        break;
      case 'top':
        this.rotateCamera(180, 90 + EPS);
        break;
      case 'bottom':
        this.rotateCamera(180, 270 + EPS);
        break;
      case 'left':
        this.rotateCamera(90, EPS);
        break;
      case 'right':
        this.rotateCamera(270, EPS);
        break;
    }

    if (offset) {
      this.moveCamera(offset.left || 0 , offset.top || 0);
    }
    this.update();
  }

  public rotate(panAngleDeg: number, tiltAngleDeg: number): void {
    this.rotateCamera(panAngleDeg, tiltAngleDeg);
    this.update();
  }

  private rotateCamera(panAngleDeg: number, tiltAngleDeg: number): void {
    this.camera.rotateOnAxis(this._xAxis, MathUtils.toRadians(-tiltAngleDeg));
    this.camera.rotateOnWorldAxis(this._yAxis, MathUtils.toRadians(panAngleDeg));
  }


  public get currentZoom() {
    return this.camera.zoom;
  }

  public set currentZoom(value: number) {
    this.camera.zoom = value;
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld(true);
  }


  private zoomCamera(value: number): void {
    if (value > 0) {
      this.camera.zoom *= Math.pow(0.95, 1);
    } else if (value < 0) {
      this.camera.zoom /= Math.pow(0.95, 1);
    }
  }

  public setCameraDefaultPosition(parameters: DefaultCameraPositionParams, savePosition = true): void {
    this.reset();
    if (!parameters) {
      return;
    }

    if (parameters.pdfMode && parameters.defaultDistancePdfCameraParameter) {
      this.camera.zoom = 1;
      const halfDistance: number = parameters.defaultDistancePdfCameraParameter / 2;
      this.camera.top = halfDistance;
      this.camera.bottom = -halfDistance;
      this.camera.left = -halfDistance * this._aspect;
      this.camera.right = halfDistance * this._aspect;
    }

    if (parameters.isViewAllInDefaultPositionRequired) {
      this.setCameraByBBox(parameters.bbox, !!parameters.pdfMode);
    }

    if (!parameters.pdfMode && parameters.defaultDistance) {
      this.camera.zoom = parameters.defaultDistance;
    }

    if ((parameters.defaultTiltAngle || parameters.defaultTiltAngle === 0) && (parameters.defaultOrbitAngle || parameters.defaultOrbitAngle === 0)) {
      this.rotateCamera(parameters.defaultOrbitAngle, parameters.defaultTiltAngle);
    }

    if (isNaN(parameters.offsetDistance)) {
      this.moveCamera((parameters.canvasWidth || 0) + (parameters.defaultViewMarginLeft || 0), 0);
    } else {
      this.moveCamera(parameters.offsetDistance, 0);
    }

    this.update();

    if (savePosition) {
      this._defaultCameraMatrix = this.camera.matrixWorld.clone();
    }
  }

  public viewByBBox(bbox: Box3): void {
    const rotation = this.camera.rotation.clone();
    this.reset();
    this.setCameraByBBox(bbox);
    this.camera.rotation.set(rotation.x, rotation.y, rotation.z)
    this.update();
  }

  private setCameraByBBox(bbox?: Box3, changeLens: boolean = false): void {
    if (!bbox) {
      return;
    }

    this.camera.zoom = 1;
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld(true);

    const centerOfBox = bbox.getCenter(new Vector3());

    this.moveCamera(-centerOfBox.x, centerOfBox.y, centerOfBox.z);

    let maxDistanceX = 0;
    let maxDistanceY = 0;

    const sceneLookAtPoint = centerOfBox.clone().project(this.camera);

    const objectsBoundingPoints = this.constuctBoundingPointsByMinMax(bbox.min, bbox.max);

    for (let point of objectsBoundingPoints) {
      const scenePoint = point.clone().project(this.camera);
      const distanceX = Math.abs(scenePoint.x - sceneLookAtPoint.x);
      const distanceY = Math.abs(scenePoint.y - sceneLookAtPoint.y);
      if (maxDistanceX < distanceX) {
        maxDistanceX = distanceX;
      }
      if (maxDistanceY < distanceY) {
        maxDistanceY = distanceY;
      }
    }

    const maxDistanceRatio = maxDistanceX / maxDistanceY;
    const zoomByWidth = maxDistanceRatio > 1;
    let targetZoomPoint = sceneLookAtPoint.clone();

    if (zoomByWidth) {
      targetZoomPoint.x += maxDistanceX;
    } else {
      targetZoomPoint.y += maxDistanceY;
    }

    targetZoomPoint = targetZoomPoint.unproject(this.camera);

    let maxDistance = centerOfBox.distanceTo(targetZoomPoint);

    this._aspect = this.canvas.clientWidth / this.canvas.clientHeight;

    if (zoomByWidth) {
      maxDistance = maxDistance / this._aspect;
    }

    const distanceWithPadding = maxDistance + (changeLens ? this._allObjectsPadding : this._padding);

    if (changeLens) {
      this.camera.top = distanceWithPadding;
      this.camera.bottom = -distanceWithPadding;
      this.camera.left = -distanceWithPadding * this._aspect;
      this.camera.right = distanceWithPadding * this._aspect;
    } else {
      this.camera.zoom = ((this.camera.top / distanceWithPadding) * 0.9);
    }
  }

  private constuctBoundingPointsByMinMax(min: Vector3, max: Vector3): Vector3[] {
    const boundingBoxPoints = [];

    boundingBoxPoints.push(new Vector3(min.x, min.y, min.z));
    boundingBoxPoints.push(new Vector3(max.x, max.y, max.z));
    boundingBoxPoints.push(new Vector3(max.x, min.y, min.z));
    boundingBoxPoints.push(new Vector3(min.x, max.y, min.z));
    boundingBoxPoints.push(new Vector3(max.x, max.y, min.z));
    boundingBoxPoints.push(new Vector3(min.x, min.y, max.z));
    boundingBoxPoints.push(new Vector3(max.x, min.y, max.z));
    boundingBoxPoints.push(new Vector3(min.x, max.y, max.z));

    return boundingBoxPoints;
  }

  public setCameraFromClone(clone: OrthographicCamera): void {
    this.reset();
    this.camera.position.set(clone.position.x, clone.position.y, clone.position.z);
    this.camera.rotation.set(clone.rotation.x, clone.rotation.y, clone.rotation.z);
    this.camera.zoom = clone.zoom;
    this.camera.top = clone.top;
    this.camera.bottom = clone.bottom;
    this.camera.left = clone.left;
    this.camera.right = clone.right;
    this.update();
  }
}
